import { useState } from "react";
import { useTheme } from "@emotion/react";
import {
  Box,
  Divider,
  IconButton,
  Link,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  InfoOutlined,
  LaunchOutlined,
  MenuBookOutlined,
} from "@mui/icons-material";
import useStyles from "./styles";

/**
 * Botão de informação (ⓘ) que abre um Popover explicando uma métrica/recurso.
 * Reutilizável em qualquer canto do app — passe o conteúdo via props.
 *
 * @param {string}   titulo        Título do popover (obrigatório).
 * @param {string}   descricao     Texto curto explicando o que é a métrica.
 * @param {string[]} comoLer       Lista de bullets "Como ler" (opcional).
 * @param {string}   legenda       Legenda exibida abaixo da ilustração (opcional).
 * @param {Function} Ilustracao    Componente SVG recebendo { theme } (opcional).
 * @param {object}   wiki          { url, label } para link externo (opcional).
 * @param {string}   iconSize      Tamanho do ícone ("small" | "medium" | "inherit").
 * @param {object}   sxIcon        sx extra para o IconButton (opcional).
 * @param {object}   anchorOrigin  Override do anchorOrigin do Popover (opcional).
 * @param {object}   transformOrigin Override do transformOrigin do Popover (opcional).
 */
export default function InfoPopover({
  titulo,
  descricao,
  comoLer,
  legenda,
  Ilustracao,
  wiki,
  iconSize = "small",
  sxIcon,
  anchorOrigin = { vertical: "bottom", horizontal: "right" },
  transformOrigin = { vertical: "top", horizontal: "right" },
}) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  if (!titulo && !descricao) return null;

  return (
    <>
      <Tooltip title="Mais informações">
        <IconButton
          size={iconSize}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ ...styles.iconButton, ...sxIcon }}
          aria-label="Mais informações"
        >
          <InfoOutlined fontSize={iconSize} />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slotProps={{ paper: { sx: styles.paper } }}
      >
        <Box sx={styles.content}>
          {titulo && <Typography sx={styles.titulo}>{titulo}</Typography>}

          {descricao && (
            <Typography sx={styles.descricao}>{descricao}</Typography>
          )}

          {Ilustracao && (
            <Box sx={styles.boxIlustracao}>
              <Ilustracao theme={theme} />
            </Box>
          )}

          {legenda && <Typography sx={styles.legenda}>{legenda}</Typography>}

          {Array.isArray(comoLer) && comoLer.length > 0 && (
            <>
              <Typography sx={styles.comoLerTitulo}>Como ler</Typography>
              <Stack component="ul" sx={styles.comoLerLista}>
                {comoLer.map((c, i) => (
                  <Typography key={i} component="li" sx={styles.comoLerItem}>
                    {c}
                  </Typography>
                ))}
              </Stack>
            </>
          )}

          {wiki?.url && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Link
                href={wiki.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={styles.link}
              >
                <MenuBookOutlined sx={{ fontSize: 16 }} />
                {wiki.label || wiki.url}
                <LaunchOutlined sx={{ fontSize: 14 }} />
              </Link>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
}
